import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Siren, Volume2, Clock3 } from "lucide-react";

function Toggle({ label, desc, checked, onChange, testId }) {
  return (
    <label className="flex items-start justify-between gap-4 rounded-sm border border-white/10 bg-black/30 p-4" data-testid={`${testId}-row`}>
      <div className="min-w-0">
        <div className="font-oswald text-sm font-bold uppercase tracking-widest text-white">{label}</div>
        <div className="mt-1 text-xs leading-relaxed text-gray-400">{desc}</div>
      </div>
      <input
        type="checkbox"
        data-testid={testId}
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-5 w-5 shrink-0 accent-[#d90429]"
      />
    </label>
  );
}

export function BroadcastFxCard() {
  const [fx, setFx] = useState(null);
  const audioRef = useRef(null);
  useEffect(() => { api.get("/broadcast/fx").then((r) => setFx(r.data)).catch(() => {}); }, []);

  const save = async (patch, msg) => {
    try {
      const { data } = await api.put("/broadcast/fx", patch);
      setFx(data);
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const previewTone = () => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.volume = 1;
    el.play().catch(() => toast.error("Tap again — the browser blocked autoplay"));
  };

  if (!fx) return null;
  return (
    <div className="glass rounded-sm border border-[#d90429]/40 p-6" data-testid="broadcast-fx-card">
      <div className="flex items-center gap-2">
        <Siren className="h-5 w-5 text-[#d90429]" />
        <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Breaking FX · Takeover &amp; On the 8s</div>
      </div>
      <p className="mt-1 text-sm text-gray-300">
        Phase 2 TV graphics. Changes are live on the stream within a few minutes (or restart the encoder to apply now).
      </p>

      <div className="mt-4 grid gap-3">
        <Toggle
          testId="fx-takeover-enabled"
          label="Breaking Weather Takeover"
          desc="A full-screen takeover slams in ahead of the alert board when a NEW Tornado, Severe Thunderstorm, or Flash Flood Warning fires."
          checked={fx.takeover_enabled}
          onChange={(v) => save({ takeover_enabled: v }, v ? "Takeover on" : "Takeover off")}
        />

        <div className="rounded-sm border border-white/10 bg-black/30 p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-oswald text-sm font-bold uppercase tracking-widest text-white"><Volume2 className="h-4 w-4 text-[#ffd60a]" /> Attention Tone</div>
              <div className="mt-1 text-xs leading-relaxed text-gray-400">A short branded chime (not the EAS tone) plays with the takeover on the stream. Music ducks out while it sounds.</div>
            </div>
            <input
              type="checkbox"
              data-testid="fx-takeover-tone"
              checked={!!fx.takeover_tone}
              onChange={(e) => save({ takeover_tone: e.target.checked }, e.target.checked ? "Tone on" : "Tone off")}
              disabled={!fx.takeover_enabled}
              className="mt-1 h-5 w-5 shrink-0 accent-[#d90429] disabled:opacity-40"
            />
          </div>
          <button
            type="button"
            data-testid="fx-preview-tone"
            onClick={previewTone}
            className="mt-3 inline-flex items-center gap-2 rounded-sm border border-white/15 px-3 py-1.5 font-oswald text-[11px] font-bold uppercase tracking-widest text-white hover:border-[#ffd60a] hover:text-[#ffd60a]"
          >
            <Volume2 className="h-3.5 w-3.5" /> Preview tone
          </button>
          <audio ref={audioRef} src={fx.tone_url} preload="auto" />
        </div>

        <Toggle
          testId="fx-eights-enabled"
          label='"On the 8s" Bumpers'
          desc="A branded forecast bumper interrupts the loop at clock times ending in 8 (:08, :18, :28…) and rolls straight into the local conditions segment."
          checked={fx.on_the_eights_enabled}
          onChange={(v) => save({ on_the_eights_enabled: v }, v ? "On the 8s on" : "On the 8s off")}
        />
      </div>

      <div className="mt-4 flex items-center gap-2 rounded-sm border border-white/10 bg-black/20 p-3 text-xs text-gray-400">
        <Clock3 className="h-4 w-4 text-[#4cc9f0]" />
        Takeovers fire once per new warning (never re-triggered on reload). The 8s bumper fires once per slot.
      </div>
    </div>
  );
}
